import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

// import { users } from 'src/_mock/user';

// import Scrollbar from 'src/components/scrollbar';

import TableNoData from "../table-no-data";
import UserTableRow from "../user-table-row";
import UserTableHead from "../user-table-head";
import TableEmptyRows from "../table-empty-rows";
import UserTableToolbar from "../user-table-toolbar";
import { emptyRows, applyFilter, getComparator } from "../utils";
import Iconify from "../../../../components/iconify/iconify";
import { useNavigate, useLocation } from "react-router-dom";
import boardServices from "../../../../services/boardServices";
import { sortBoardMembersByRank, getBoardMemberOrder } from "../../constants";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils";
import { TableHeaderStyle } from "../../../../theme/palette";

// ----------------------------------------------------------------------


export default function Board() {
  const [page, setPage] = useState(0);

  const [order, setOrder] = useState("asc");

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState("displayOrder");

  const [filterName, setFilterName] = useState("");

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [data, setData] = useState([]);
  const [endTermDialogOpen, setEndTermDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [endDateValue, setEndDateValue] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === "asc";
    if (id !== "") {
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(id);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data?.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const dataFiltered = applyFilter({
    inputData: data,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const notFound = !dataFiltered.length && !!filterName;
  const totalFiltered = dataFiltered.length;
  const dataPaginated = dataFiltered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Start date from API or from MongoDB ObjectId (first 8 hex chars = creation timestamp)
  const getDisplayStartDate = (row) => {
    if (row?.effectiveStartDate || row?.startDate || row?.createdAt)
      return row.effectiveStartDate || row.startDate || row.createdAt;
    const id = row?._id;
    if (!id) return null;
    const hex = typeof id === "string" ? id : (id?.toString && id.toString());
    if (!hex || hex.length < 8) return null;
    const sec = parseInt(hex.slice(0, 8), 16);
    if (Number.isNaN(sec)) return null;
    return new Date(sec * 1000).toISOString();
  };

  const getData = () => {
    boardServices
      .get()
      .then((res) => {
        const list = res?.data?.data || [];
        const withRank = list.map((m) => ({ ...m, displayOrder: getBoardMemberOrder(m) }));
        setData(sortBoardMembersByRank(withRank));
      })
      .catch(() => {
        setData([]);
      });
  };

  // Refetch when viewing this page (e.g. after returning from CreateBoard)
  useEffect(() => {
    if (location.pathname === "/admin/board") {
      getData();
    }
  }, [location.pathname]);


  const handleDeleteRow = (row) => {
    boardServices.deleteBoardMember(row._id).then(() => {
      toast.success("Deleted")
      getData();
    }).catch((err) => {
      toast.error(getErrorMessage(err))
    });
  }

  const handleEditRow = (row) => {
    navigate(`EditBoard/${row._id}`);
  };

  const handleOpenEndTerm = (row) => {
    setSelectedRow(row);
    setEndDateValue(new Date().toISOString().slice(0, 10));
    setEndTermDialogOpen(true);
  };

  const handleCloseEndTerm = () => {
    setEndTermDialogOpen(false);
    setSelectedRow(null);
    setEndDateValue("");
  };

  const handleConfirmEndTerm = () => {
    if (!selectedRow || !endDateValue) return;
    boardServices
      .endTerm(selectedRow._id, endDateValue)
      .then(() => {
        toast.success("Board member term ended; moved to archive.");
        handleCloseEndTerm();
        getData();
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleMakeCurrent = (row) => {
    boardServices
      .makeCurrent(row._id)
      .then(() => {
        toast.success("Board member set to current.");
        getData();
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  return (
    <div>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Stack>
          <Typography variant="h4">Board Members</Typography>
          <div style={{
            backgroundColor: TableHeaderStyle.border,
            height: "2px"
          }} />
        </Stack>

        <Button
          onClick={() => navigate("CreateBoard")}
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' }, fontWeight: 'bold' }}
        >
          New Board Member
        </Button>
      </Stack>

      <Card>
        <UserTableToolbar
          numSelected={selected.length}
          filterName={filterName}
          onFilterName={handleFilterByName}
        />

        <TableContainer sx={{ overflow: "unset" }}>
          <Table sx={{ minWidth: 800 }}>
            <UserTableHead
              order={order}
              orderBy={orderBy}
              rowCount={dataPaginated?.length || 0}
              numSelected={selected.length}
              onRequestSort={handleSort}
              onSelectAllClick={handleSelectAllClick}
              headLabel={[
                { id: "#", label: "#", width: "5%" },
                { id: "name", label: "Name" },
                { id: "title", label: "Title" },
                { id: "image", label: "Image" },
                { id: "effectiveStartDate", label: "Start date" },
                { id: "endDate", label: "End date" },
                { id: "status", label: "Status" },
                { id: "" },
              ]}
            />
            <TableBody>
              {dataPaginated.map((row, index) => (
                  <UserTableRow
                    key={row._id}
                    rowNumber={page * rowsPerPage + index + 1}
                    row={row}
                    name={row.name}
                    title={row.title}
                    image={row.image}
                    startDate={getDisplayStartDate(row)}
                    endDate={row.endDate}
                    onDelete={() => handleDeleteRow(row)}
                    onEdit={() => handleEditRow(row)}
                    onEndTerm={() => handleOpenEndTerm(row)}
                    onMakeCurrent={() => handleMakeCurrent(row)}
                  />
                ))}

              <TableEmptyRows
                height={77}
                emptyRows={emptyRows(page, rowsPerPage, totalFiltered)}
              />

              {notFound && <TableNoData query={filterName} />}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          page={page}
          component="div"
          count={data?.length}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Dialog open={endTermDialogOpen} onClose={handleCloseEndTerm} maxWidth="xs" fullWidth>
        <DialogTitle>End board member term</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set the end date for <strong>{selectedRow.name}</strong>. They will appear in the Past Members archive.
            </Typography>
          )}
          <TextField
            label="End date"
            type="date"
            value={endDateValue}
            onChange={(e) => setEndDateValue(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEndTerm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmEndTerm}
            sx={{
              bgcolor: "#28a745",
              "&:hover": { bgcolor: "#218838" },
            }}
          >
            End term
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
